import { Button } from "@/components/ui/button";
import { Calendar, Shield, Award, Clock, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BookDemoAnchor } from "@/components/BookDemoAnchor";
import { useRotatingCTA } from "@/hooks/useRotatingCTA";

const FinalCTA = () => {
  const { t } = useTranslation();
  const { ctaIndex, rotatingCTAs, getAppUrl } = useRotatingCTA();

  // Trust badges must not claim ISO certification or bank-level security.
  const trustBadges = [
    { icon: Award, label: t("shared.trustBadges.euGdprCompliant") },
    { icon: Clock, label: t("shared.trustBadges.trialFourteenDays") },
    { icon: Shield, label: t("shared.trustBadges.euHosting") },
  ];

  return (
    <section className="section-padding bg-gradient-to-br from-primary to-primary-hover text-primary-foreground" id="contact">
      <div className="container-custom text-center">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Headline */}
          <div className="space-y-4">
            <h2 className="text-section">
              {t("pages.home.finalCTA.title")}
            </h2>
            <p className="text-xl opacity-90">
              {t("pages.home.finalCTA.subtitle")}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              variant="secondary" 
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold px-8 py-3 text-lg group"
            >
              <a 
                href={getAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="button-final-cta-primary"
              >
                <span className="flex items-center gap-2">
                  <span 
                    key={ctaIndex}
                    className="animate-in fade-in slide-in-from-bottom-2 duration-500"
                  >
                    {rotatingCTAs[ctaIndex]}
                  </span>
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </span>
              </a>
            </Button>
            <BookDemoAnchor
              source="home-final"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-11 px-8 py-3 text-lg border-2 border-primary-foreground text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary"
              data-testid="button-final-cta-demo"
            >
              <Calendar className="w-5 h-5 mr-2" />
              {t("pages.home.finalCTA.secondaryCTA")}
            </BookDemoAnchor>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-8 pt-8 opacity-80">
            {trustBadges.map((badge, index) => {
              const IconComponent = badge.icon;
              return (
                <div key={index} className="flex items-center justify-center space-x-2">
                  <IconComponent className="w-5 h-5" />
                  <span className="text-sm font-medium">{badge.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
